import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  cardStyle,
  ghostButtonStyle,
  ghostLinkStyle,
  inputStyle,
  primaryButtonStyle,
} from '@kilat/ui';
import { Link } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

import { useAuth } from '../lib/use-auth.js';
import { useI18n, type I18n } from '../lib/use-i18n.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { voucherTemplatesRepoFor } from '../lib/repositories/vouchers-repo.js';
import { customerTagsRepoFor } from '../lib/repositories/customer-tags-repo.js';
import { callBulkIssueVouchers, type BulkIssueVouchersOutput } from '../lib/callables/loyalty.js';
import { logger } from '../lib/logger.js';
import { defaultTiers } from '@kilat/config-seed';
import { money, type CustomerTag, type VoucherEffect, type VoucherTemplate } from '@kilat/shared';

/**
 * /loyalty/vouchers — operator-side voucher template CRUD + bulk issue.
 *
 * Sprint 16: full targeting authoring + Edit mode.
 * Voucher bulk-issue: each template gets a "Bulk issue" action that opens an
 * audience picker (all customers / by tag / by tier), a confirm step, then
 * calls `bulkIssueVouchers` and renders a result summary (issued / skipped with
 * reasons / failed) with full idle→confirm→running→success/error states.
 *
 * Honest-effects stance (unchanged): only pct_off / fixed_off are authorable
 * (cart-only) — the redemption engine `computeVoucherDiscount` honours only
 * those. free_service / free_package + template `totalLimit` are deferred (see
 * docs/features/voucher-bulk-issue.md).
 *
 * Targeting fields surfaced inline (mirror the server-side enforcement in
 * `issueVoucher` + the pure `evaluateTargeting` helper):
 *   - tierMin: None / Bronze / Silver / Gold (mapped to rank 0|1|2)
 *   - newCustomerOnly: checkbox
 *   - customerTagIn: multi-select chips drawn from /loyalty/tags
 */
export function VouchersPage() {
  const { claims } = useAuth();
  const t = useI18n();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  const templatesSubscribe = useMemo(() => {
    if (!tenantId) return null;
    const repo = voucherTemplatesRepoFor(tenantId);
    return (cb: (vs: VoucherTemplate[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);

  const { data: templates, loading } = useTrackedSnapshot<VoucherTemplate[]>(
    ['vouchers', tenantId],
    templatesSubscribe,
  );

  const tagsSubscribe = useMemo(() => {
    if (!tenantId) return null;
    const repo = customerTagsRepoFor(tenantId);
    return (cb: (vs: CustomerTag[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);
  const { data: tags } = useTrackedSnapshot<CustomerTag[]>(
    ['customerTags', tenantId],
    tagsSubscribe,
  );
  const activeTags = useMemo(() => (tags ?? []).filter((tg) => !tg.archived), [tags]);

  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [bulkTemplate, setBulkTemplate] = useState<VoucherTemplate | null>(null);

  if (!tenantId) return <Screen body={t.t('dashboard.vouchers.signInPrompt')} />;
  if (loading) return <Screen body={t.t('common.loading')} />;

  const list = (templates ?? []).slice().sort((a, b) => {
    const ams = a.createdAt?.toMillis?.() ?? 0;
    const bms = b.createdAt?.toMillis?.() ?? 0;
    return bms - ams;
  });

  const editingTemplate = editingTemplateId
    ? (list.find((tmpl) => tmpl.id === editingTemplateId) ?? null)
    : null;

  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--tint-subtle)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <header
        style={{
          marginBottom: 'var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 0 }}>
            {t.t('dashboard.vouchers.title')}
          </h1>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              margin: 0,
            }}
          >
            {t.t(
              list.length === 1 ? 'dashboard.vouchers.countOne' : 'dashboard.vouchers.countOther',
              { count: list.length },
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Link to="/loyalty/tags" style={ghostLinkStyle}>
            {t.t('dashboard.vouchers.customerTagsLink')}
          </Link>
          <Link to="/queue" style={ghostLinkStyle}>
            {t.t('dashboard.vouchers.backToQueue')}
          </Link>
        </div>
      </header>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-6)',
          alignItems: 'flex-start',
        }}
      >
        <section style={{ ...cardStyle, flex: '2 1 320px', minWidth: 0 }}>
          <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 600, margin: 0 }}>
            {t.t('dashboard.vouchers.activeTemplates')}
          </h2>
          <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)' }}>
            {list.map((tmpl) => (
              <TemplateRow
                key={tmpl.id}
                tenantId={tenantId}
                template={tmpl}
                tagsById={Object.fromEntries((tags ?? []).map((tg) => [tg.id, tg]))}
                isEditing={tmpl.id === editingTemplateId}
                onEdit={() => setEditingTemplateId(tmpl.id)}
                onBulkIssue={() => setBulkTemplate(tmpl)}
              />
            ))}
            {list.length === 0 ? (
              <p
                style={{
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                  textAlign: 'center',
                  margin: `var(--space-6) 0`,
                }}
              >
                {t.t('dashboard.vouchers.empty')}
              </p>
            ) : null}
          </div>
        </section>

        <section style={{ ...cardStyle, flex: '1 1 320px', minWidth: 0, maxWidth: 460 }}>
          <TemplateForm
            tenantId={tenantId}
            editing={editingTemplate}
            availableTags={activeTags}
            onDone={() => setEditingTemplateId(null)}
            onCancelEdit={() => setEditingTemplateId(null)}
          />
        </section>
      </div>

      {bulkTemplate ? (
        <BulkIssueModal
          tenantId={tenantId}
          template={bulkTemplate}
          availableTags={activeTags}
          onClose={() => setBulkTemplate(null)}
        />
      ) : null}
    </main>
  );
}

// ── Template row ────────────────────────────────────────────────────────

function TemplateRow({
  tenantId,
  template,
  tagsById,
  isEditing,
  onEdit,
  onBulkIssue,
}: {
  readonly tenantId: string;
  readonly template: VoucherTemplate;
  readonly tagsById: Record<string, CustomerTag>;
  readonly isEditing: boolean;
  readonly onEdit: () => void;
  readonly onBulkIssue: () => void;
}) {
  const t = useI18n();
  const [busy, setBusy] = useState(false);
  const name = template.name[t.locale] ?? template.name['en'] ?? template.id;
  const effectLabel = describeEffect(template.effect, t);
  const expiresAt = template.validity.to.toMillis?.() ?? template.validity.to.seconds * 1000;
  const expired = expiresAt < Date.now();
  const pill = expired
    ? {
        bg: 'rgba(239,68,68,0.10)',
        fg: 'var(--color-accent)',
        label: t.t('dashboard.vouchers.status.expired'),
      }
    : template.active
      ? {
          bg: 'rgba(13,122,79,0.10)',
          fg: 'var(--color-primary)',
          label: t.t('dashboard.vouchers.status.active'),
        }
      : {
          bg: 'var(--color-border)',
          fg: 'var(--color-on-surface-muted)',
          label: t.t('dashboard.vouchers.status.paused'),
        };
  const targetingSummary = describeTargeting(template.targeting, tagsById, t);
  const canBulkIssue = template.active && !expired;

  async function toggleActive() {
    setBusy(true);
    try {
      await voucherTemplatesRepoFor(tenantId).update(template.id, {
        active: !template.active,
        updatedAt: Timestamp.now(),
      });
    } catch (e) {
      logger.error('voucher toggle failed', { error: String(e), id: template.id });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: isEditing ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
        background: isEditing ? 'rgba(13,122,79,0.04)' : 'var(--color-surface)',
      }}
    >
      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
        <p style={{ margin: 0, fontWeight: 500, overflowWrap: 'anywhere' }}>{name}</p>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t.t('dashboard.vouchers.row.meta', {
            effect: effectLabel,
            limit: template.perCustomerLimit,
            date: new Date(expiresAt).toLocaleDateString(),
          })}
        </p>
        {targetingSummary ? (
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t.t('dashboard.vouchers.row.targets', { summary: targetingSummary })}
          </p>
        ) : null}
      </div>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}
      >
        <span
          style={{
            background: pill.bg,
            color: pill.fg,
            padding: '4px 10px',
            borderRadius: 'var(--radius-pill)',
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
          }}
        >
          {pill.label}
        </span>
        {expired ? null : (
          <button
            type="button"
            onClick={() => void toggleActive()}
            disabled={busy}
            style={miniGhostButtonStyle}
          >
            {template.active
              ? t.t('dashboard.vouchers.row.pause')
              : t.t('dashboard.vouchers.row.resume')}
          </button>
        )}
        {canBulkIssue ? (
          <button type="button" onClick={onBulkIssue} style={miniGhostButtonStyle}>
            {t.t('dashboard.vouchers.row.bulkIssue')}
          </button>
        ) : null}
        <button type="button" onClick={onEdit} disabled={isEditing} style={miniGhostButtonStyle}>
          {isEditing ? t.t('dashboard.vouchers.row.editing') : t.t('dashboard.vouchers.row.edit')}
        </button>
      </div>
    </div>
  );
}

// ── Bulk issue modal ────────────────────────────────────────────────────

type BulkStep = 'audience' | 'confirm' | 'running' | 'result' | 'error';
type AudienceKind = 'all' | 'tag' | 'tier';

function BulkIssueModal({
  tenantId,
  template,
  availableTags,
  onClose,
}: {
  readonly tenantId: string;
  readonly template: VoucherTemplate;
  readonly availableTags: readonly CustomerTag[];
  readonly onClose: () => void;
}) {
  const t = useI18n();
  const templateName = template.name[t.locale] ?? template.name['en'] ?? template.id;
  // Tier picker offers Silver+ / Gold+ (rank ≥ 1); Bronze-min ≈ "all".
  const tierOptions = useMemo(() => defaultTiers.filter((ti) => ti.rank >= 1), []);

  const [step, setStep] = useState<BulkStep>('audience');
  const [kind, setKind] = useState<AudienceKind>('all');
  const [tagId, setTagId] = useState<string | null>(availableTags[0]?.id ?? null);
  const [tierMin, setTierMin] = useState<string>(tierOptions[0]?.id ?? 'silver');
  const [result, setResult] = useState<BulkIssueVouchersOutput | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Esc-to-close (only when not mid-flight so a running batch isn't abandoned).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && step !== 'running') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, step]);

  const tierName = (id: string): string => {
    const ti = defaultTiers.find((x) => x.id === id);
    return ti ? (ti.name[t.locale] ?? ti.name['en']) : id;
  };
  const tagLabel = (id: string | null): string => {
    if (!id) return '';
    const tg = availableTags.find((x) => x.id === id);
    return tg ? (tg.label[t.locale] ?? tg.label['en'] ?? tg.id) : id;
  };

  const audienceSummary = (): string => {
    if (kind === 'tag') {
      return t.t('dashboard.vouchers.bulk.audienceTagSummary', { label: tagLabel(tagId) });
    }
    if (kind === 'tier') {
      return t.t('dashboard.vouchers.bulk.audienceTierSummary', { tier: tierName(tierMin) });
    }
    return t.t('dashboard.vouchers.bulk.audienceAllSummary');
  };

  const canReview = kind !== 'tag' || !!tagId;

  async function run() {
    setStep('running');
    setErrorMsg(null);
    try {
      const audience =
        kind === 'tag'
          ? ({ kind: 'tag', tagId: tagId ?? '' } as const)
          : kind === 'tier'
            ? ({ kind: 'tier', tierMin } as const)
            : ({ kind: 'all' } as const);
      const out = await callBulkIssueVouchers({ tenantId, templateId: template.id, audience });
      setResult(out);
      setStep('result');
    } catch (e) {
      setErrorMsg((e as Error).message ?? String(e));
      setStep('error');
    }
  }

  return (
    <ModalShell
      title={t.t('dashboard.vouchers.bulk.title', { name: templateName })}
      onClose={onClose}
    >
      {step === 'audience' ? (
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          <div>
            <p style={{ margin: 0, fontWeight: 500 }}>
              {t.t('dashboard.vouchers.bulk.audienceTitle')}
            </p>
            <p style={helpTextStyle}>{t.t('dashboard.vouchers.bulk.audienceHelp')}</p>
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <button
              type="button"
              aria-pressed={kind === 'all'}
              onClick={() => setKind('all')}
              style={chipStyle(kind === 'all')}
            >
              {t.t('dashboard.vouchers.bulk.all')}
            </button>
            <button
              type="button"
              aria-pressed={kind === 'tag'}
              onClick={() => setKind('tag')}
              style={chipStyle(kind === 'tag')}
            >
              {t.t('dashboard.vouchers.bulk.byTag')}
            </button>
            <button
              type="button"
              aria-pressed={kind === 'tier'}
              onClick={() => setKind('tier')}
              style={chipStyle(kind === 'tier')}
            >
              {t.t('dashboard.vouchers.bulk.byTier')}
            </button>
          </div>

          {kind === 'all' ? (
            <p style={helpTextStyle}>{t.t('dashboard.vouchers.bulk.allHelp')}</p>
          ) : null}

          {kind === 'tag' ? (
            availableTags.length === 0 ? (
              <p style={helpTextStyle}>{t.t('dashboard.vouchers.bulk.noTags')}</p>
            ) : (
              <label style={fieldGrid}>
                <span style={fieldLabel}>{t.t('dashboard.vouchers.bulk.pickTag')}</span>
                <select
                  value={tagId ?? ''}
                  onChange={(e) => setTagId(e.target.value)}
                  style={inputStyle}
                >
                  {availableTags.map((tg) => (
                    <option key={tg.id} value={tg.id}>
                      {tg.label[t.locale] ?? tg.label['en'] ?? tg.id}
                    </option>
                  ))}
                </select>
              </label>
            )
          ) : null}

          {kind === 'tier' ? (
            <label style={fieldGrid}>
              <span style={fieldLabel}>{t.t('dashboard.vouchers.bulk.pickTier')}</span>
              <select
                value={tierMin}
                onChange={(e) => setTierMin(e.target.value)}
                style={inputStyle}
              >
                {tierOptions.map((ti) => (
                  <option key={ti.id} value={ti.id}>
                    {t.t('dashboard.vouchers.bulk.tierAndAbove', {
                      tier: ti.name[t.locale] ?? ti.name['en'],
                    })}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <div style={modalActionsStyle}>
            <button
              type="button"
              onClick={() => setStep('confirm')}
              disabled={!canReview}
              style={{ ...primaryButtonStyle, opacity: canReview ? 1 : 0.5 }}
            >
              {t.t('dashboard.vouchers.bulk.review')}
            </button>
            <button type="button" onClick={onClose} style={ghostButtonStyle}>
              {t.t('dashboard.vouchers.bulk.cancel')}
            </button>
          </div>
        </div>
      ) : null}

      {step === 'confirm' ? (
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          <p style={{ margin: 0, fontWeight: 500 }}>
            {t.t('dashboard.vouchers.bulk.confirmTitle')}
          </p>
          <dl style={{ margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
            <div style={confirmRowStyle}>
              <dt style={fieldLabel}>{t.t('dashboard.vouchers.bulk.confirmTemplate')}</dt>
              <dd style={{ margin: 0, fontWeight: 500 }}>{templateName}</dd>
            </div>
            <div style={confirmRowStyle}>
              <dt style={fieldLabel}>{t.t('dashboard.vouchers.bulk.confirmAudience')}</dt>
              <dd style={{ margin: 0, fontWeight: 500 }}>{audienceSummary()}</dd>
            </div>
          </dl>
          <p style={helpTextStyle}>{t.t('dashboard.vouchers.bulk.confirmWarning')}</p>
          <div style={modalActionsStyle}>
            <button type="button" onClick={() => void run()} style={primaryButtonStyle}>
              {t.t('dashboard.vouchers.bulk.issue')}
            </button>
            <button type="button" onClick={() => setStep('audience')} style={ghostButtonStyle}>
              {t.t('dashboard.vouchers.bulk.back')}
            </button>
          </div>
        </div>
      ) : null}

      {step === 'running' ? (
        <p role="status" style={{ margin: 0, color: 'var(--color-on-surface-muted)' }}>
          {t.t('dashboard.vouchers.bulk.running')}
        </p>
      ) : null}

      {step === 'result' && result ? (
        <BulkResult result={result} onDone={onClose} onIssueMore={() => setStep('audience')} />
      ) : null}

      {step === 'error' ? (
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          <div>
            <p style={{ margin: 0, fontWeight: 600, color: 'var(--color-accent)' }}>
              {t.t('dashboard.vouchers.bulk.errorTitle')}
            </p>
            {errorMsg ? <p style={helpTextStyle}>{errorMsg}</p> : null}
          </div>
          <div style={modalActionsStyle}>
            <button type="button" onClick={() => void run()} style={primaryButtonStyle}>
              {t.t('dashboard.vouchers.bulk.errorRetry')}
            </button>
            <button type="button" onClick={onClose} style={ghostButtonStyle}>
              {t.t('dashboard.vouchers.bulk.errorClose')}
            </button>
          </div>
        </div>
      ) : null}
    </ModalShell>
  );
}

function BulkResult({
  result,
  onDone,
  onIssueMore,
}: {
  readonly result: BulkIssueVouchersOutput;
  readonly onDone: () => void;
  readonly onIssueMore: () => void;
}) {
  const t = useI18n();
  const nothingMatched = result.audienceRequested === 0;
  const capCount = result.skipped.filter((s) => s.reason === 'per_customer_cap').length;
  const targetingCount = result.skipped.filter((s) => s.reason === 'targeting').length;
  const otherCount = result.skipped.length - capCount - targetingCount;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <p style={{ margin: 0, fontWeight: 600 }}>{t.t('dashboard.vouchers.bulk.resultTitle')}</p>

      {nothingMatched ? (
        <p style={helpTextStyle}>{t.t('dashboard.vouchers.bulk.none')}</p>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <p style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>
            {t.t(
              result.issued === 1
                ? 'dashboard.vouchers.bulk.issuedOne'
                : 'dashboard.vouchers.bulk.issuedOther',
              { count: result.issued },
            )}
          </p>

          {result.skipped.length > 0 ? (
            <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <p style={{ margin: 0, fontWeight: 500 }}>
                {t.t(
                  result.skipped.length === 1
                    ? 'dashboard.vouchers.bulk.skippedOne'
                    : 'dashboard.vouchers.bulk.skippedOther',
                  { count: result.skipped.length },
                )}
              </p>
              <ul style={reasonListStyle}>
                {capCount > 0 ? (
                  <li>{t.t('dashboard.vouchers.bulk.reasonCap', { count: capCount })}</li>
                ) : null}
                {targetingCount > 0 ? (
                  <li>
                    {t.t('dashboard.vouchers.bulk.reasonTargeting', { count: targetingCount })}
                  </li>
                ) : null}
                {otherCount > 0 ? (
                  <li>{t.t('dashboard.vouchers.bulk.reasonOther', { count: otherCount })}</li>
                ) : null}
              </ul>
            </div>
          ) : null}

          {result.failed.length > 0 ? (
            <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <p style={{ margin: 0, fontWeight: 500, color: 'var(--color-accent)' }}>
                {t.t(
                  result.failed.length === 1
                    ? 'dashboard.vouchers.bulk.failedOne'
                    : 'dashboard.vouchers.bulk.failedOther',
                  { count: result.failed.length },
                )}
              </p>
              <p style={helpTextStyle}>{t.t('dashboard.vouchers.bulk.retryHint')}</p>
            </div>
          ) : null}

          {result.truncated ? (
            <p style={helpTextStyle}>
              {t.t('dashboard.vouchers.bulk.truncated', {
                processed: result.audienceProcessed,
                requested: result.audienceRequested,
              })}
            </p>
          ) : null}
        </div>
      )}

      <div style={modalActionsStyle}>
        <button type="button" onClick={onDone} style={primaryButtonStyle}>
          {t.t('dashboard.vouchers.bulk.done')}
        </button>
        <button type="button" onClick={onIssueMore} style={ghostButtonStyle}>
          {t.t('dashboard.vouchers.bulk.issueMore')}
        </button>
      </div>
    </div>
  );
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
}) {
  const t = useI18n();
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-4)',
        zIndex: 200,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-3)',
          padding: 'var(--space-5)',
          maxWidth: 480,
          width: '100%',
          maxHeight: '90dvh',
          overflowY: 'auto',
          display: 'grid',
          gap: 'var(--space-4)',
        }}
      >
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 'var(--space-3)',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t.t('dashboard.vouchers.bulk.close')}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 18,
              cursor: 'pointer',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {'×'}
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

// ── Template form (create + edit) ───────────────────────────────────────

function TemplateForm({
  tenantId,
  editing,
  availableTags,
  onDone,
  onCancelEdit,
}: {
  readonly tenantId: string;
  readonly editing: VoucherTemplate | null;
  readonly availableTags: readonly CustomerTag[];
  readonly onDone: () => void;
  readonly onCancelEdit: () => void;
}) {
  const t = useI18n();
  // Form state — initialised from `editing` when provided so toggling Edit on
  // a row prefills everything.
  const initial = useMemo(() => formFromTemplate(editing), [editing]);
  const [name, setName] = useState(initial.name);
  const [type, setType] = useState<'pct_off' | 'fixed_off'>(initial.type);
  const [value, setValue] = useState(initial.value);
  const [perCustomer, setPerCustomer] = useState(initial.perCustomer);
  const [expiresDays, setExpiresDays] = useState(initial.expiresDays);
  const [tierMin, setTierMin] = useState<number | null>(initial.tierMin);
  const [newCustomerOnly, setNewCustomerOnly] = useState(initial.newCustomerOnly);
  const [selectedTags, setSelectedTags] = useState<Set<string>>(initial.tagsSet);

  // Whenever `editing` changes (toggle Edit on a different row, or Cancel),
  // reset every field. Watching the id covers both transitions.
  useEffect(() => {
    setName(initial.name);
    setType(initial.type);
    setValue(initial.value);
    setPerCustomer(initial.perCustomer);
    setExpiresDays(initial.expiresDays);
    setTierMin(initial.tierMin);
    setNewCustomerOnly(initial.newCustomerOnly);
    setSelectedTags(initial.tagsSet);
  }, [editing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);

  function toggleTag(id: string) {
    const next = new Set(selectedTags);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedTags(next);
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSavedNotice(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(t.t('dashboard.vouchers.form.errName'));
      return;
    }
    const valNum = Number(value);
    if (!Number.isFinite(valNum) || valNum <= 0) {
      setError(t.t('dashboard.vouchers.form.errValue'));
      return;
    }
    if (type === 'pct_off' && valNum > 100) {
      setError(t.t('dashboard.vouchers.form.errPct'));
      return;
    }
    const limitNum = Math.max(1, Math.round(Number(perCustomer)));
    const daysNum = Math.max(1, Math.round(Number(expiresDays)));

    setSubmitting(true);
    try {
      const now = Timestamp.now();
      const expiresAt = Timestamp.fromMillis(Date.now() + daysNum * 24 * 60 * 60 * 1000);
      const effect: VoucherEffect =
        type === 'pct_off'
          ? { type: 'pct_off', value: Math.round(valNum), appliesTo: 'cart' }
          : { type: 'fixed_off', value: Math.round(valNum * 100), appliesTo: 'cart' };

      const targeting: VoucherTemplate['targeting'] = {
        ...(typeof tierMin === 'number' ? { tierMin } : {}),
        ...(newCustomerOnly ? { newCustomerOnly: true } : {}),
        ...(selectedTags.size > 0 ? { customerTagIn: Array.from(selectedTags) } : {}),
      };

      if (editing) {
        // Edit: preserve id + createdAt + existing description. Reset validity
        // window relative to TODAY because that matches operator mental model
        // ("extend by 30 days"). Operators who want a precise to-date can
        // request a date-picker later.
        await voucherTemplatesRepoFor(tenantId).set(editing.id, {
          ...editing,
          name: { ...(editing.name ?? {}), en: trimmedName },
          description:
            editing.description && Object.keys(editing.description).length > 0
              ? editing.description
              : { en: trimmedName },
          effect,
          validity: { from: editing.validity.from, to: expiresAt },
          perCustomerLimit: limitNum,
          targeting,
          updatedAt: now,
        });
        setSavedNotice(t.t('dashboard.vouchers.form.updated', { name: trimmedName }));
        onDone();
      } else {
        const id = `vch-${Math.random().toString(36).slice(2, 8)}`;
        await voucherTemplatesRepoFor(tenantId).set(id, {
          id,
          kind: 'template',
          name: { en: trimmedName },
          description: { en: trimmedName },
          effect,
          validity: { from: now, to: expiresAt },
          perCustomerLimit: limitNum,
          targeting,
          active: true,
          createdAt: now,
          updatedAt: now,
        });
        setSavedNotice(t.t('dashboard.vouchers.form.created', { name: trimmedName }));
        // Reset form
        setName('');
        setValue('10');
        setPerCustomer('1');
        setExpiresDays('30');
        setTierMin(null);
        setNewCustomerOnly(false);
        setSelectedTags(new Set());
      }
    } catch (e2) {
      logger.error('voucher save failed', { error: String(e2), editing: editing?.id });
      setError((e2 as Error).message ?? t.t('dashboard.vouchers.form.errSave'));
    } finally {
      setSubmitting(false);
    }
  }

  const isEditMode = editing !== null;

  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <header>
        <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 600, margin: 0 }}>
          {isEditMode
            ? t.t('dashboard.vouchers.form.editTitle', {
                name: editing.name['en'] ?? editing.id,
              })
            : t.t('dashboard.vouchers.form.createTitle')}
        </h2>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            margin: 'var(--space-1) 0 0 0',
          }}
        >
          {isEditMode
            ? t.t('dashboard.vouchers.form.editSubtitle')
            : t.t('dashboard.vouchers.form.createSubtitle')}
        </p>
      </header>

      {savedNotice ? (
        <p
          role="status"
          style={{
            margin: 0,
            color: 'var(--color-primary)',
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
          }}
        >
          {savedNotice}
        </p>
      ) : null}

      <label style={fieldGrid}>
        <span style={fieldLabel}>{t.t('dashboard.vouchers.form.nameLabel')}</span>
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t.t('dashboard.vouchers.form.namePlaceholder')}
          style={inputStyle}
        />
      </label>

      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={fieldLabel}>{t.t('dashboard.vouchers.form.discountType')}</legend>
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
          <button
            type="button"
            aria-pressed={type === 'pct_off'}
            onClick={() => setType('pct_off')}
            style={chipStyle(type === 'pct_off')}
          >
            {t.t('dashboard.vouchers.form.pctChip')}
          </button>
          <button
            type="button"
            aria-pressed={type === 'fixed_off'}
            onClick={() => setType('fixed_off')}
            style={chipStyle(type === 'fixed_off')}
          >
            {t.t('dashboard.vouchers.form.rmChip')}
          </button>
        </div>
      </fieldset>

      <label style={fieldGrid}>
        <span style={fieldLabel}>
          {type === 'pct_off'
            ? t.t('dashboard.vouchers.form.percent')
            : t.t('dashboard.vouchers.form.ringgit')}
        </span>
        <input
          type="number"
          min={1}
          max={type === 'pct_off' ? 100 : undefined}
          step="any"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
        />
      </label>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: 'var(--space-3)',
        }}
      >
        <label style={fieldGrid}>
          <span style={fieldLabel}>{t.t('dashboard.vouchers.form.perCustomer')}</span>
          <input
            type="number"
            min={1}
            step={1}
            value={perCustomer}
            onChange={(e) => setPerCustomer(e.target.value)}
            style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
          />
        </label>
        <label style={fieldGrid}>
          <span style={fieldLabel}>
            {isEditMode
              ? t.t('dashboard.vouchers.form.expiresFromToday')
              : t.t('dashboard.vouchers.form.expires')}
          </span>
          <input
            type="number"
            min={1}
            step={1}
            value={expiresDays}
            onChange={(e) => setExpiresDays(e.target.value)}
            style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
          />
        </label>
      </div>

      {/* Targeting section */}
      <div
        style={{
          padding: 'var(--space-4)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          display: 'grid',
          gap: 'var(--space-3)',
        }}
      >
        <p style={{ margin: 0, fontWeight: 500 }}>{t.t('dashboard.vouchers.form.whoTitle')}</p>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t.t('dashboard.vouchers.form.whoHelp')}
        </p>

        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend style={fieldLabel}>{t.t('dashboard.vouchers.form.minTier')}</legend>
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-2)',
              marginTop: 'var(--space-2)',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              aria-pressed={tierMin === null}
              onClick={() => setTierMin(null)}
              style={chipStyle(tierMin === null)}
            >
              {t.t('dashboard.vouchers.form.noMinimum')}
            </button>
            {defaultTiers.map((tier) => (
              <button
                key={tier.id}
                type="button"
                aria-pressed={tierMin === tier.rank}
                onClick={() => setTierMin(tier.rank)}
                style={chipStyle(tierMin === tier.rank)}
              >
                {t.t('dashboard.vouchers.form.tierChip', {
                  tier: tier.name[t.locale] ?? tier.name['en'],
                })}
              </button>
            ))}
          </div>
        </fieldset>

        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <input
            type="checkbox"
            checked={newCustomerOnly}
            onChange={(e) => setNewCustomerOnly(e.target.checked)}
            style={{ width: 18, height: 18, cursor: 'pointer' }}
          />
          <span style={fieldLabel}>{t.t('dashboard.vouchers.form.newOnly')}</span>
        </label>

        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend style={fieldLabel}>{t.t('dashboard.vouchers.form.tagsLegend')}</legend>
          {availableTags.length === 0 ? (
            <p
              style={{
                margin: 'var(--space-2) 0 0 0',
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
              }}
            >
              {t.t('dashboard.vouchers.form.noTagsLead')}
              <Link to="/loyalty/tags" style={{ color: 'var(--color-primary)' }}>
                {t.t('dashboard.vouchers.form.noTagsLink')}
              </Link>
              {t.t('dashboard.vouchers.form.noTagsTail')}
            </p>
          ) : (
            <div
              style={{
                display: 'flex',
                gap: 'var(--space-2)',
                marginTop: 'var(--space-2)',
                flexWrap: 'wrap',
              }}
            >
              {availableTags.map((tg) => {
                const on = selectedTags.has(tg.id);
                return (
                  <button
                    key={tg.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleTag(tg.id)}
                    style={chipStyle(on, tg.colour)}
                  >
                    {tg.label[t.locale] ?? tg.label['en'] ?? tg.id}
                  </button>
                );
              })}
            </div>
          )}
        </fieldset>
      </div>

      {error ? (
        <p
          role="alert"
          style={{ color: 'var(--color-accent)', fontSize: 'var(--text-meta)', margin: 0 }}
        >
          {error}
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <button
          type="submit"
          disabled={submitting}
          style={{
            ...primaryButtonStyle,
            opacity: submitting ? 0.6 : 1,
            cursor: submitting ? 'wait' : 'pointer',
          }}
        >
          {submitting
            ? t.t('dashboard.vouchers.form.saving')
            : isEditMode
              ? t.t('dashboard.vouchers.form.saveChanges')
              : t.t('dashboard.vouchers.form.create')}
        </button>
        {isEditMode ? (
          <button
            type="button"
            onClick={onCancelEdit}
            disabled={submitting}
            style={ghostButtonStyle}
          >
            {t.t('dashboard.vouchers.form.cancelEdit')}
          </button>
        ) : null}
      </div>
    </form>
  );
}

// ── helpers ────────────────────────────────────────────────────────────

interface FormState {
  name: string;
  type: 'pct_off' | 'fixed_off';
  value: string;
  perCustomer: string;
  expiresDays: string;
  tierMin: number | null;
  newCustomerOnly: boolean;
  tagsSet: Set<string>;
}

function formFromTemplate(t: VoucherTemplate | null): FormState {
  if (!t) {
    return {
      name: '',
      type: 'pct_off',
      value: '10',
      perCustomer: '1',
      expiresDays: '30',
      tierMin: null,
      newCustomerOnly: false,
      tagsSet: new Set(),
    };
  }
  const type: 'pct_off' | 'fixed_off' = t.effect.type === 'fixed_off' ? 'fixed_off' : 'pct_off';
  const value =
    t.effect.type === 'pct_off'
      ? String(t.effect.value)
      : t.effect.type === 'fixed_off'
        ? String(t.effect.value / 100)
        : '0';
  const expiresMs =
    t.validity.to?.toMillis?.() ?? ((t.validity.to?.seconds ?? 0) * 1000 || Date.now());
  const daysRemaining = Math.max(1, Math.round((expiresMs - Date.now()) / (24 * 60 * 60 * 1000)));
  return {
    name: t.name['en'] ?? t.id,
    type,
    value,
    perCustomer: String(t.perCustomerLimit),
    expiresDays: String(daysRemaining),
    tierMin: typeof t.targeting?.tierMin === 'number' ? t.targeting.tierMin : null,
    newCustomerOnly: t.targeting?.newCustomerOnly === true,
    tagsSet: new Set(t.targeting?.customerTagIn ?? []),
  };
}

function describeEffect(effect: VoucherEffect, t: I18n): string {
  switch (effect.type) {
    case 'pct_off':
      return t.t('dashboard.vouchers.effect.pctOff', { value: effect.value });
    case 'fixed_off':
      return t.t('dashboard.vouchers.effect.fixedOff', { amount: money.format(effect.value) });
    case 'free_service':
      return t.t('dashboard.vouchers.effect.freeService');
    case 'free_package':
      return t.t('dashboard.vouchers.effect.freePackage');
  }
}

function describeTargeting(
  targeting: VoucherTemplate['targeting'] | undefined,
  tagsById: Record<string, CustomerTag>,
  t: I18n,
): string {
  if (!targeting) return '';
  const parts: string[] = [];
  if (typeof targeting.tierMin === 'number' && targeting.tierMin > 0) {
    const tier = defaultTiers.find((x) => x.rank === targeting.tierMin);
    const tierName = tier ? (tier.name[t.locale] ?? tier.name['en']) : String(targeting.tierMin);
    parts.push(t.t('dashboard.vouchers.targeting.tier', { tier: tierName }));
  }
  if (targeting.newCustomerOnly) parts.push(t.t('dashboard.vouchers.targeting.newOnly'));
  if (targeting.customerTagIn && targeting.customerTagIn.length > 0) {
    const labels = targeting.customerTagIn.map(
      (id) => tagsById[id]?.label[t.locale] ?? tagsById[id]?.label['en'] ?? id,
    );
    parts.push(t.t('dashboard.vouchers.targeting.tag', { labels: labels.join(' / ') }));
  }
  return parts.join(' · ');
}

function Screen({ body }: { readonly body: string }) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <p style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>{body}</p>
    </main>
  );
}

const fieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const fieldGrid: React.CSSProperties = { display: 'grid', gap: 'var(--space-2)', minWidth: 0 };

const helpTextStyle: React.CSSProperties = {
  margin: 'var(--space-1) 0 0 0',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const modalActionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2)',
  flexWrap: 'wrap',
};

const confirmRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 120px) minmax(0, 1fr)',
  gap: 'var(--space-3)',
  alignItems: 'baseline',
};

const reasonListStyle: React.CSSProperties = {
  margin: 0,
  paddingLeft: 'var(--space-4)',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  display: 'grid',
  gap: 'var(--space-1)',
};

const miniGhostButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--color-on-surface-muted)',
  padding: '6px 10px',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-meta)',
  cursor: 'pointer',
};

function chipStyle(selected: boolean, accent?: string): React.CSSProperties {
  const ring = accent ?? 'var(--color-primary)';
  return {
    padding: '8px 14px',
    borderRadius: 'var(--radius-pill)',
    border: `1px solid ${selected ? ring : 'var(--color-border-strong)'}`,
    background: selected ? (accent ?? 'var(--color-primary)') : 'transparent',
    color: selected ? (accent ? 'white' : 'var(--color-primary-fg)') : 'var(--color-on-surface)',
    fontSize: 'var(--text-meta)',
    fontWeight: 500,
    cursor: 'pointer',
  };
}
